import { ErrorIcon, InfoFilledIcon } from '@econsult/econsult-ui/dist/assets';
import { InputMessageIcon } from '@econsult/econsult-ui/dist/components/Input/InputStyles';
import { STRINGS } from 'config';
import React from 'react';
import {
  CharacterCounter,
  ContentFieldsWrapper,
  ErrorMessage,
  ErrorMessageWrapper,
  FormItemLabel,
  FormLabelContainer,
  RadioInput,
  RadioLabel,
  RadioLegend,
  RadioWrapper,
  TemplateTypeInfo,
  TextArea,
  TextAreaWrapper,
  Validation,
} from './formItemsStyles';

const { PERSONAL, SHARED } = STRINGS.TEMPLATE.TEMPLATE_TYPE;

type TranscriptionType = 'Consultation' | 'Dictation';

interface TranscriptionTypeFieldsProps {
  idPrefix: string;
  transcriptionType: TranscriptionType;
  setTranscriptionType: (type: 'Consultation' | 'Dictation') => void;
  disabled?: boolean;
}

type TemplateType = typeof PERSONAL | typeof SHARED;

interface VisibilityFieldsProps {
  idPrefix: string;
  templateType: TemplateType;
  setTemplateType: (type: typeof PERSONAL | typeof SHARED) => void;
  showError: boolean;
  disabled?: boolean;
}

interface ContentFieldsProps {
  content: string;
  onChangeContent: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  idPrefix: string;
  contentErrorMessage?: string;
  disabled?: boolean;
}

export const TranscriptionTypeFields: React.FC<TranscriptionTypeFieldsProps> = ({
  idPrefix,
  transcriptionType,
  setTranscriptionType,
  disabled = false,
}) => (
  <RadioWrapper>
    <RadioLegend>Transcription type</RadioLegend>
    <RadioLabel htmlFor={`${idPrefix}-transcriptionType-consultation`}>
      <RadioInput
        id={`${idPrefix}-transcriptionType-consultation`}
        name="transcriptionType"
        value="Consultation"
        checked={transcriptionType === 'Consultation'}
        onChange={() => setTranscriptionType('Consultation')}
        data-automation="TranscriptionTypeRadioButtonConsultation"
        disabled={disabled}
      />
      <span>Consultation</span>
    </RadioLabel>

    <RadioLabel htmlFor={`${idPrefix}-transcriptionType-dictation`}>
      <RadioInput
        id={`${idPrefix}-transcriptionType-dictation`}
        name="transcriptionType"
        value="Dictation"
        checked={transcriptionType === 'Dictation'}
        onChange={() => setTranscriptionType('Dictation')}
        data-automation="TranscriptionTypeRadioButtonDictation"
        disabled={disabled}
      />
      <span>Dictation</span>
    </RadioLabel>
  </RadioWrapper>
);

export const VisibilityFields: React.FC<VisibilityFieldsProps> = ({
  idPrefix,
  templateType,
  setTemplateType,
  showError,
  disabled = false,
}) => (
  <RadioWrapper>
    <RadioLegend>Type</RadioLegend>
    <RadioLabel htmlFor={`${idPrefix}-templateVisibility-personal`}>
      <RadioInput
        id={`${idPrefix}-templateVisibility-personal`}
        name="personalOrShared"
        value="personal"
        checked={templateType === PERSONAL}
        onChange={() => setTemplateType(PERSONAL)}
        data-automation="TemplateManagerVisibilityRadioButtonPersonal"
        disabled={disabled}
      />
      <span>Personal</span>
    </RadioLabel>

    <RadioLabel htmlFor={`${idPrefix}-templateVisibility-public`}>
      <RadioInput
        id={`${idPrefix}-templateVisibility-public`}
        name="personalOrShared"
        value="shared"
        checked={templateType === SHARED}
        onChange={() => setTemplateType(SHARED)}
        data-automation="TemplateManagerVisibilityRadioButtonPublic"
        disabled={disabled}
      />
      <span>Shared</span>
    </RadioLabel>

    {showError && (
      <ErrorMessageWrapper data-automation="TemplateManagerVisibilityError">
        <InputMessageIcon>
          <ErrorIcon />
        </InputMessageIcon>
        <ErrorMessage>Please select template type</ErrorMessage>
      </ErrorMessageWrapper>
    )}

    {templateType && (
      <TemplateTypeInfo data-automation="TemplateManagerVisibilityInfo">
        {templateType === PERSONAL
          ? 'This template will be available to just you alone.'
          : 'This template will be available for everyone in your practice.'}
      </TemplateTypeInfo>
    )}
  </RadioWrapper>
);

export const ContentFields: React.FC<ContentFieldsProps> = ({
  content,
  onChangeContent,
  idPrefix,
  contentErrorMessage,
  disabled = false,
}) => (
  <ContentFieldsWrapper>
    <TextAreaWrapper>
      {contentErrorMessage && (
        <Validation data-automation="TemplateContentTextAreaError">
          {contentErrorMessage}
          <InfoFilledIcon />
        </Validation>
      )}
      <FormLabelContainer>
        <FormItemLabel>Section headings</FormItemLabel>
      </FormLabelContainer>
      <TextArea
        data-hj-suppress
        id={`${idPrefix}-content-textarea`}
        data-automation="TemplateManagerContentInput"
        placeholder="Put each heading on a new line"
        value={content}
        onChange={onChangeContent}
        aria-label="Edit message template content"
        disabled={disabled}
      />
    </TextAreaWrapper>
    <CharacterCounter
      id={`${idPrefix}-content-messageCharCounter`}
      charCount={content.length}
      maxChars={500}
      data-automation={`${idPrefix}CharacterCounter`}
    >
      {content.length}
    </CharacterCounter>
  </ContentFieldsWrapper>
);
